import { useEffect, useState } from 'react'
import { ICellRendererParams } from '@ag-grid-community/core'
import { Button, Spinner } from 'components/common'
import Modal from 'components/modal'
import toast from 'react-hot-toast'
import {
  deleteFreshCategory,
  fetchFreshCategoryUsage,
} from 'utils/api/freshCategoryApi'
import { handleError } from 'utils/helpers/handleError'

type Props = {
  isOpen: boolean
  closeModal: () => void
  params?: ICellRendererParams
}

const ConfirmDeleteFreshCategoryModal = ({
  params,
  closeModal,
  isOpen,
}: Props) => {
  // null while loading - the admin is told what deleting costs before
  // confirming, not after
  const [assignedUsers, setAssignedUsers] = useState<number | null>(null)
  const [assignedItems, setAssignedItems] = useState<number>(0)

  const categoryId = params?.data?._id
  const categoryName = params?.data?.name

  useEffect(() => {
    if (!isOpen || !categoryId) return

    setAssignedUsers(null)
    fetchFreshCategoryUsage(categoryId)
      .then(({ data }) => {
        setAssignedUsers(data?.assignedUsers ?? 0)
        setAssignedItems(data?.assignedItems ?? 0)
      })
      .catch(() => {
        setAssignedUsers(0)
        setAssignedItems(0)
      })
  }, [isOpen, categoryId])

  const cancelDelete = () => {
    if (categoryId) {
      const rowNode = params?.api.getRowNode(categoryId)
      rowNode?.setSelected(false)
    }
    closeModal()
  }

  const handleDelete = async () => {
    if (!params || !categoryId) return

    const deleteToast = toast.loading('Deleting category...', {
      duration: Infinity,
    })
    try {
      await deleteFreshCategory(categoryId)
      const rowNode = params.api.getRowNode(categoryId)
      params.api.applyTransaction({ remove: [rowNode?.data] })
      toast.success(`Category "${categoryName}" deleted successfully!`, {
        duration: 3000,
        id: deleteToast,
      })
      closeModal()
    } catch (error) {
      handleError(error, deleteToast, {
        default: 'Failed to delete category.',
      })
    }
  }

  return (
    <Modal
      width={550}
      title={`Confirm delete "${categoryName}" ?`}
      titleClassname="text-xl font-semibold max-w-xl"
      isOpen={isOpen}
      closeModal={cancelDelete}
      customStyle={{ padding: '25px 40px' }}
    >
      {assignedUsers === null ? (
        <Spinner />
      ) : (
        <>
          <div>
            Are you sure you want to delete <strong>{categoryName}</strong>?
            This action cannot be undone.
          </div>

          {assignedItems > 0 && (
            <div className="mt-3 rounded-lg bg-yellow-50 p-3 text-sm text-yellow-800">
              <strong>{assignedItems}</strong>{' '}
              {assignedItems === 1 ? 'item is' : 'items are'} assigned this
              category. Deleting clears it from all of them, and the only way
              back is to re-import the assignments.
            </div>
          )}

          {assignedUsers > 0 && (
            <div className="mt-3 rounded-lg bg-yellow-50 p-3 text-sm text-yellow-800">
              <strong>{assignedUsers}</strong>{' '}
              {assignedUsers === 1 ? 'user is' : 'users are'} assigned this
              category. Deleting removes it from them. To keep their
              assignments, mark the category Inactive instead.
            </div>
          )}

          <div className="mt-6 flex w-full items-center gap-5">
            <Button
              onClick={handleDelete}
              className="w-full"
              buttonText="Confirm"
              type="button"
            />
            <Button
              className="w-full"
              buttonText="Cancel"
              variant="outline"
              type="button"
              onClick={cancelDelete}
            />
          </div>
        </>
      )}
    </Modal>
  )
}

export default ConfirmDeleteFreshCategoryModal
